/**
 * The SHAPE of a chip on a library row — geometry only, no state (VERA-260).
 *
 * ## Why this exists
 *
 * A library row now carries TWO chip rows: the `Display as` controls
 * (`AnalyticsDisplayPicker`) and the solution badges (`AnalyticsCustomize`). They sit four
 * pixels apart in a 340px panel, so any disagreement in padding, radius or text size reads
 * as one of them being broken rather than as two components. §36's rule is to extract on
 * the SECOND hand-roll, and the badges are it.
 *
 * ## What it deliberately does NOT carry
 *
 * Colour, cursor, hover and border COLOUR — including a transparent one — all stay at the
 * call sites, because the two rows are different KINDS of thing and their differences are
 * the point:
 *
 *   `Display as`      a CONTROL. Outlined on a transparent ground, `cursor: grab`, a hover
 *                     tint, and a grip glyph that replaces its category mark on hover. Its
 *                     border and fill also encode §34's selectable-card axis (tint =
 *                     considering, brand border = chosen); an incapable chip renders
 *                     `text.disabled` with a reason.
 *   Solution badges   METADATA. A quiet fill and NO visible edge (`borderColor:
 *                     'transparent'`), `cursor: default`, no hover, not focusable.
 *
 * ⚠️ THAT ASYMMETRY IS THE POINT, and it did not exist at first: both rows shipped with
 * the same `primary.100` edge on the same transparent ground, so a passive tag and a
 * clickable-and-draggable control were pixel-identical eight pixels apart. Filled-vs-
 * outlined is what tells them apart now. **Do not "tidy" the badge's transparent border
 * into a shared `border: 'none'`, and do not give the badge an edge back.**
 *
 * The transparent border is also why this module can stay geometry-only: the badge keeps
 * the border's 1px in its box model, so both pills are still exactly 24px tall and exactly
 * as wide as their content plus the same padding. Appearance diverges; geometry does not.
 *
 * Folding those into one "variant" prop would mean a shared component that takes a flag
 * and branches on it in six places — which is how a shared component becomes harder to
 * read than the two literals it replaced. Geometry is the part that genuinely must not
 * drift; appearance is the part that legitimately differs.
 *
 * ## The numbers
 *
 * Taken from the `Display as` chip as it already shipped, not re-picked, so this is a pure
 * extraction and that row is pixel-identical afterwards. 12/500 matches
 * `AnalyticsCategoryLabel`, which is the panel's established size for metadata text.
 *
 * `minHeight` rather than `height`: a chip whose label wraps at a narrow panel must grow
 * rather than clip its own text (§49's argument, one level down — a badge IS its label).
 */
export const ANALYTICS_CHIP_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  px: 2,
  py: 0.5,
  m: 0,
  font: 'inherit',
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1,
  minHeight: 24,
  borderRadius: 1.5,
  border: '1px solid',
  '& svg': { flexShrink: 0 },
} as const

/**
 * The gap between chips in a row, and between the rows themselves.
 *
 * ⚠️ AN ABSOLUTE STRING, consumed as `useFlexGap` + `gap` rather than as `spacing`.
 * `spacing` compiles to `margin-left`, which a WRAPPED chip carries onto the next line and
 * which therefore indents every row after the first — the bug `AnalyticsDisplayPicker`
 * already documents at its own `Stack`. Both chip rows wrap, so both need this.
 */
export const ANALYTICS_CHIP_GAP = '8px'
